import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { QrCode, Shield, Eye, EyeOff } from "lucide-react";
import useAxios from "@/hooks/use-axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminLogin() {

  const api = useAxios()
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await api.post(`${API}/admin/login`, formData);
      localStorage.setItem("admin_token", response.data.access_token);
      toast.success("Login successful");
      navigate("/admin");
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Invalid credentials"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex flex-col">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-dark-foreground/50 backdrop-blur-md">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img className="h-8" src="/assets/logo_garudaqr.png" />
            
          </Link>
        </div>
      </header>

      {/* Login Form */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="tech-border rounded-lg p-8 bg-dark-foreground">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-primary-500/20 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-primary-500" />
              </div>
              <h1 class Name="text-2xl font-bold text-white">
                Admin Login
              </h1>
              <p className="text-sm text-zinc-400 mt-2">
                Enter your credentials to access the dashboard
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="username" className="text-zinc-300">
                  Username
                </Label>
                <Input
                  id="username"
                  type="text"
                  value={formData.username}
                  onChange={(e) =>
                    setFormData({ ...formData, username: e.target.value })
                  }
                  className="mt-1 bg-zinc-950 border-zinc-800"
                  placeholder="Enter username"
                  required
                  data-testid="username-input"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-zinc-300">
                  Password
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    className="bg-zinc-950 border-zinc-800 pr-10"
                    placeholder="Enter password"
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                    data-testid="toggle-password"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-primary-500 hover:bg-primary-600 text-white btn-hover h-11"
                data-testid="submit-btn"
              >
                {isLoading ? (
                  <div className="spinner" />
                ) : (
                  "Login"
                )}
              </Button>
            </form>
          </div>

          <p className="text-center text-xs text-zinc-500 mt-6">
            <Link to="/" className="hover:text-primary-400 transition-colors">
              ← Back to Demo
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
